import { SyntheticEvent, useContext, useState, useMemo, useEffect } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Typography, Box, Tab } from "@mui/material";
import QuotesAudits from "../../pages/audits/QuotesAudits";
import MatrixAudits from "../../pages/audits/Matrix/MatrixAudits";
import FailedOrderLogs from "../../pages/orders/FailedOrderLogs";
import { ECOMMERCE, INTERNAL, MARKETPLACE, MERCHANT } from "../../utils/constants";
import { UserContext } from "../../contexts/userContext";
import StatusChangesAudits from "../../pages/audits/StatusChangesAudits";
import { useSearchParams } from "react-router-dom";

export const AUDIT_TAB_VALUES: { [key: string]: string } = {
  QUOTES: "quotes",
  ORDERS: "orders",
  MATRICES: "matrices",
  STATUS_CHANGES: "status-changes",
};

const BLACKLISTED_TABS_BY_ENTITY: Record<string, string[]> = {
  [MARKETPLACE]: [],
  [MERCHANT]: [AUDIT_TAB_VALUES.ORDERS],
  [ECOMMERCE]: [AUDIT_TAB_VALUES.ORDERS, AUDIT_TAB_VALUES.MATRICES],
  [INTERNAL]: [],
};

const getInitialTabFromParams = (tabParam: string | null, entity: string) => {
  const blacklisted = BLACKLISTED_TABS_BY_ENTITY[entity] || [];
  const allowed = Object.values(AUDIT_TAB_VALUES).filter(
    (v) => !blacklisted.includes(v)
  );
  return tabParam && allowed.includes(tabParam) ? tabParam : AUDIT_TAB_VALUES.QUOTES;
};

const AuditsTabs = () => {
  const {
    currentUser: { entity },
  } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const isMarketplace = useMemo(() => entity === MARKETPLACE, [entity]);
  const showMatricesTab = useMemo(() => entity !== ECOMMERCE, [entity]);

  const initialTab = getInitialTabFromParams(searchParams.get("tab"), entity);
  const [value, setValue] = useState<string>(initialTab);

  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setValue(newValue);
    const next = new URLSearchParams(searchParams);
    next.set("tab", newValue);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const desired = getInitialTabFromParams(searchParams.get("tab"), entity);
    setValue((prev) => (prev === desired ? prev : desired));
  }, [entity, searchParams]);

  return (
    <>
      <Typography className="f-s-20">Auditoría</Typography>
      <TabContext value={value}>
        <Box
          className="m-t-30"
          sx={{ borderBottom: 1, borderColor: "divider", width: "fit-content" }}
        >
          <TabList onChange={handleChange}>
            <Tab label="Cotizaciones" value={AUDIT_TAB_VALUES.QUOTES} />
            {showMatricesTab && <Tab label="Matrices" value={AUDIT_TAB_VALUES.MATRICES} />}
            <Tab label="Cambios de estado" value={AUDIT_TAB_VALUES.STATUS_CHANGES} />
            {isMarketplace && (
              <Tab label="Órdenes Fallidas" value={AUDIT_TAB_VALUES.ORDERS} />
            )}
          </TabList>
        </Box>

        <TabPanel value={AUDIT_TAB_VALUES.QUOTES}>
          <QuotesAudits />
        </TabPanel>
        {showMatricesTab && (
          <TabPanel value={AUDIT_TAB_VALUES.MATRICES}>
            <MatrixAudits />
          </TabPanel>
        )}

        <TabPanel value={AUDIT_TAB_VALUES.STATUS_CHANGES}>
          <StatusChangesAudits />
        </TabPanel>

        {isMarketplace && (
          <TabPanel value={AUDIT_TAB_VALUES.ORDERS}>
            <FailedOrderLogs />
          </TabPanel>
        )}
      </TabContext>
    </>
  );
};

export default AuditsTabs;
